// V2-Templates / V2-Squads embed config + token acquisition. Instance of the V2-GAC embed pattern (see
// utils/v2gac.ts, which documents the security model in detail; utils/v2player.ts is the closest sibling).
//
// 🔴 ONE APP, TWO TABS. "V2-Templates" and "V2-Squads" both embed HotUtilsV2-Templates — the same container,
// the same audience ("templates"), the same token — at two different PATHS (`/templates` and `/squads`).
// The embedded app selects its page from the path the iframe loads. There is deliberately no second
// audience: regular vs squad is a presentation flag on the template row, resolved inside V2, and the
// website does not need to know about it. A token minted for one tab therefore works for the other.
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST templates/v2/token`, added
// to the existing TemplateController) — the server derives playerId and the GOH guildId from the
// authenticated session and stamps aud="templates". The signing secret never reaches the browser.
//
// DEV FALLBACK (opt-in only): signDevTemplatesToken() mints the HS256 token IN THE BROWSER with a shared
// secret — forgeable, never in production. Enable explicitly with REACT_APP_V2TEMPLATES_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-Templates app's origin, per environment. Override with REACT_APP_V2TEMPLATES_URL.
export function getV2TemplatesUrl(): string {
  const override = process.env.REACT_APP_V2TEMPLATES_URL;
  if (override) return override;

  const host = window.location.host;
  if (host.includes('localhost')) return 'http://localhost:5179'; // local Templates dev server

  // Dev FQDN from the first real deploy (2026-08-17, verified serving: healthz 200, whoami echo, live
  // library through the managed identity). Each app has its own Container Apps environment, so the suffix
  // is unique per app — re-read it with
  // `az containerapp show -n hotutils-templates-<env> -g rg-hotutils-templates-<env> --query properties.configuration.ingress.fqdn`.
  //
  // 🔴 The prod entry is a PLACEHOLDER that FAILS LOUDLY — nothing prod-side exists yet (no RG, no identity,
  // no production branch). ⚠️ Never point it at another app's URL "temporarily": that app would receive a
  // token whose aud is "templates", reject it, and the symptom would look like a signing problem rather
  // than a wrong URL. The tab is gated OFF outside development by isV2TemplatesEnabled() either way.
  if (host.startsWith('development')) return 'https://hotutils-templates-dev.redmushroom-9ab34414.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-templates-prod.invalid';
}

export function getV2TemplatesOrigin(): string {
  return new URL(getV2TemplatesUrl()).origin;
}

/**
 * The V2-Templates and V2-Squads tabs are DEV-ONLY for now — only surfaced (tabs + routes) on the
 * Development site and in local dev; suppressed on staging/production until ready. Same gating rule as
 * V2-GAC, V2-Events and V2-TB.
 *
 * ⚠️ Gate the ROUTE and the MENU on this identically, or a tab appears where it cannot work.
 */
export function isV2TemplatesEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function useTemplatesDevSigner(): boolean {
  return process.env.REACT_APP_V2TEMPLATES_DEV_SIGN === 'true';
}

interface TemplatesV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token (`POST templates/v2/token` on TemplateController).
 * The server derives playerId + the GOH guildId from the session and signs with aud="templates"; the browser
 * only ever receives the finished JWT.
 */
export async function fetchTemplatesToken(sessionId?: string): Promise<string> {
  const resp: TemplatesV2TokenResponse = await BaseAPI.postToApi('templates/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('Templates token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2TemplatesTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
  guildId?: string;
}

// The shared HS256 secret the Templates BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the
// app's .env.example. Override with REACT_APP_V2TEMPLATES_DEV_SECRET. Dev fallback signer only.
function getDevSecret(): string {
  return process.env.REACT_APP_V2TEMPLATES_DEV_SECRET || 'dev-shared-secret-change-me';
}

// The GOH guild id (string) for the DEV SIGNER ONLY. The website client only has the legacy numeric guild
// id, so a dev-signed token's guild scope must come from env config — deliberately NO default: the
// production path (HotService) derives the real guild server-side.
//
// ⚠️ Unlike TB, a MISSING guild is legitimate here: templates are player-scoped, and a guildless player can
// still author and run their own. Only the app's guild-run view needs it, and it reports the absence itself.
function getDevGuildId(): string | undefined {
  return process.env.REACT_APP_V2TEMPLATES_DEV_GUILD_ID || undefined;
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the Templates BFF will verify. aud MUST be "templates" — the BFF is
 * configured with PARENT_TOKEN_AUDIENCE=templates, so a GAC, Player or TB token can never authorize this app
 * and vice versa. Every app shares ONE signing secret; the audience is the only separation.
 */
export async function signDevTemplatesToken(claims: V2TemplatesTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const devGuildId = getDevGuildId();
  const payload = {
    ...(devGuildId ? { guildId: devGuildId } : {}),
    ...claims,
    iss: 'hotutils',
    aud: 'templates',
    iat: now,
    exp: now + 60 * 60, // 1h
  };
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
